<template>
  <ElementsFormField :id="id" :name="name" :error="!!error">
    <label class="checkbox" :class="[{ 'checkbox--rounded': rounded, 'checkbox--transparent': transparent }, `checkbox--size-${size}`]">
      <input
        v-bind="$attrs"
        :type="type"
        :name="name"
        :id="id ?? name"
        :checked="modelValue"
        @change="$emit('update:modelValue', ($event.target as HTMLInputElement).checked)"
      />
      <span v-if="label || $slots.default">
        <slot>{{ label }}</slot>
      </span>
    </label>
  </ElementsFormField>
</template>

<script setup lang="ts">
  withDefaults(
    defineProps<{
      label?: string
      name?: string
      id?: string
      error?: string
      type?: "checkbox" | "radio"
      size?: "small" | "base" | "large"
      rounded?: boolean
      transparent?: boolean
      modelValue?: boolean
    }>(),
    {
      type: "checkbox",
      size: "base"
    }
  )

  defineEmits(["update:modelValue"])
</script>

<style lang="scss" scoped>
  .checkbox {
    display: flex;
    align-items: center;
    gap: calc(var(--input-gap) + 2px);
    width: fit-content;

    input {
      display: inline-flex;
      flex: none;
      cursor: pointer;
      background-color: #ffffff;
      appearance: none;
      transition: all 0.2s;
      background-size: 14px;
      background-position: center;
      background-repeat: no-repeat;
      border: 1px solid var(--status-color, #bdbdbd);
      outline: none;
      border-radius: 0;

      &:checked {
        border-color: #282828;
        background-color: #282828;
        background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z"></path></svg>');
      }
      &:not(:checked):focus-within {
        border-color: var(--status-color, #b3b3b3);
      }
    }
    span {
      color: var(--status-color, #282828);
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: -0.12px;
      cursor: pointer;

      :deep(p) {
        margin: 0;
      }
      :deep(strong),
      :deep(b) {
        font-weight: 600;
      }
      :deep(a) {
        text-decoration-line: underline;
      }
    }

    &--transparent {
      input {
        background-color: transparent;
      }
    }
    &--size {
      &-small {
        input {
          width: 16px;
          height: 16px;
        }
      }
      &-base {
        input {
          width: 18px;
          height: 18px;
        }
      }
      &-large {
        input {
          width: 20px;
          height: 20px;
        }
      }
    }
    &--rounded {
      input {
        border-radius: 50%;
      }
    }

    &:has(:disabled) {
      cursor: not-allowed;
      opacity: 0.5;
    }

    @include media("md") {
      &--size {
        &-small {
          input {
            width: 14px;
            height: 14px;
          }
        }
        &-base {
          input {
            width: 16px;
            height: 16px;
          }
        }
        &-large {
          input {
            width: 18px;
            height: 18px;
          }
        }
      }
    }
  }
</style>
